import React from 'react';
import ReactFlow, { MiniMap, Controls, Background, Handle, Position } from 'reactflow';
import 'reactflow/dist/style.css';
import NivoScatterChart from "../../../charts/nivo/NivoScatterChart";
import { modernFintechPalette } from "../../../charts/nivo/utils";
import { Card, Typography } from "antd";
import Chip from "../../../components/Chip";
import {useScreenHeight} from "../../../hooks/useScreen";

const { Title } = Typography;

// Enhanced Group Node
const GroupNode = ({ data }) => {
  const color = data.color || modernFintechPalette[0];

  return (
    <div style={{ width: '300px' }}>
      <Handle type="target" position={Position.Left} />
      <Card className="shadow-lg">
        <div style={{ padding: '6px 12px' }}>
          <Title level={4} style={{ color, marginBottom: '12px', marginTop: 0 }}>
            {data.label}
          </Title>
          {data.items && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
              {data.items.slice(0, 3).map((item, i) => (
                  <Chip key={i} color={color} label={item.feature.toUpperCase()} value={(item.loading*100).toFixed(2)} />
              ))}
              {data.items.length > 3 && (
                <div style={{ fontSize: '12px', color: '#666' }}>
                  +{data.items.length - 3} more
                </div>
              )}
            </div>
          )}
        </div>
      </Card>
      <Handle type="source" position={Position.Right} />
    </div>
  );
};

// Scatter Node with your working implementation
const ScatterNode = ({ data }) => {
  return (
    <div style={{ width: '600px', height: '600px', padding: '20px', background: '#ffffff' }} className={'shadow-sm round-2'}>
      <Handle type="target" position={Position.Left} />
      <NivoScatterChart data={data.scatterData} keys={data.keys} showLabels={true} />
    </div>
  );
};

const nodeTypes = {
  groupNode: GroupNode,
  scatterNode: ScatterNode,
};

const PcaFlow = ({ groups, onNodeClick }) => {
  const screenHeight = useScreenHeight();
  const scatterData = Object.keys(groups).map((groupName) => {
    const group = groups[groupName];
    return {
      id: groupName,
      data: group.map((item) => ({ x: item.x, y: item.y, label: item.feature })),
    };
  });

  // Enhanced nodes with group information
  const nodes = Object.keys(groups).map((groupKey, index) => ({
    id: groupKey,
    type: 'groupNode',
    data: {
      label: groupKey,
      items: groups[groupKey],
      color: modernFintechPalette[index % modernFintechPalette.length]
    },
    position: { x: 50, y: index * 200 }, // Increased spacing
  }));

  // Add scatter node
  nodes.push({
    id: 'scatter',
    type: 'scatterNode',
    data: {
      scatterData,
      keys: Object.keys(groups),
    },
    position: { x: 700, y: 0 }, // Centered vertically
  });

  // Enhanced edges with better styling
  const edges = Object.keys(groups).map((groupKey, index) => ({
    id: `e-${groupKey}-scatter`,
    source: groupKey,
    target: 'scatter',
    animated: true,
    style: {
      stroke: modernFintechPalette[index % modernFintechPalette.length],
      strokeWidth: 2
    },
    type: 'smoothstep'
  }));

  return (
    <div style={{ height: Math.min(screenHeight*0.7, nodes.length * 200) }} className={'bordered'}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodeClick={(event, node) => onNodeClick && onNodeClick(node)}
        nodeTypes={nodeTypes}
        fitView
        minZoom={0.2}
        maxZoom={2}
        defaultViewport={{ zoom: 0.75 }}
      >
        <MiniMap
          nodeColor={(node) => {
            return node.data?.color || modernFintechPalette[0];
          }}
          maskColor="rgba(255, 255, 255, 0.8)"
        />
        <Controls />
        <Background gap={12} size={1} />
      </ReactFlow>
    </div>
  );
};

export default PcaFlow;